import React, { useState, useEffect, useMemo } from 'react';
import { AbsenceCodesSettings } from './AbsenceCodesSettings';
import { PauseSettings } from './PauseSettings';
import { PlanningBackupSettings } from './PlanningBackupSettings';
import { lireReglagesPause, type ReglagesPause } from '../../lib/planningPause';
import { X, Plus, Trash2, Edit2, Save, GripVertical, Check, Clock, LayoutTemplate, CalendarDays, UserX, UserPlus, CalendarOff, ListOrdered, BadgePercent, Coffee, Archive } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { useUndo } from '../ui/UndoBar';
import { ConfirmModal } from '../ui/ConfirmModal';
import type { ConfirmRequest } from '../ui/ConfirmModal';
import { Template, ShiftServiceType, TimeSlot, ABSENCE_TYPES } from './types';
import { planningApi } from '../../lib/api';
import { format, parseISO } from 'date-fns';

interface PlanningSettingsProps {
    isOpen: boolean;
    onClose: () => void;
    onDataChanged?: (options?: { applyWeeklyDefaults?: boolean; changedEmployees?: string[] }) => void;
    employees: any[];
    roles: { id: string, label: string }[];
    canManageGlobalTypes?: boolean;
}

type PlanningSettingsPayload = {
    weeklyDefaults?: Record<string, Record<string, string>>;
    absenceCodes?: { code: string; isFullDay: boolean; autoApply?: boolean; color?: string }[];
    [key: string]: any;
};

const PASTEL_COLORS = ['#60b4ff', '#c7d0e9', '#ffe39b', '#7fd13b', '#94efe3', '#ff0000', '#F5DCCF'];
const DAYS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
const SERVICE_LABELS: Record<string, string> = { 'midi+soir': 'Midi + Soir', midi: 'Midi', soir: 'Soir' };

// Primes horaires automatiques : appliquées au calcul du coût de la semaine (cf. PlanningModule) selon
// l'heure de début du segment travaillé et le jour — soir/nuit se chevauchent volontairement (un créneau
// après 22h compte pour les deux, cumul assumé), weekend s'applique tout le samedi et le dimanche.
type PremiumRule = { enabled: boolean; afterHour: string; percent: number };
type WeekendPremiumRule = { enabled: boolean; percent: number };
type PremiumRules = { soir: PremiumRule; nuit: PremiumRule; weekend: WeekendPremiumRule };
const DEFAULT_PREMIUM_RULES: PremiumRules = {
    soir: { enabled: false, afterHour: '18:00', percent: 10 },
    nuit: { enabled: false, afterHour: '22:00', percent: 25 },
    weekend: { enabled: false, percent: 20 },
};

const PlanningSettings: React.FC<PlanningSettingsProps> = ({ isOpen, onClose, onDataChanged, roles, employees, canManageGlobalTypes = false }) => {
    const [activeTab, setActiveTab] = useState<'templates' | 'defaults' | 'absenceCodes' | 'extraTypes' | 'longAbsences' | 'order' | 'premiums' | 'pause' | 'backup'>('templates');
    const [templates, setTemplates] = useState<Template[]>([]);
    const { push: pushUndo } = useUndo();
    const [confirmRequest, setConfirmRequest] = useState<ConfirmRequest | null>(null);
    const [settingsPayload, setSettingsPayload] = useState<PlanningSettingsPayload>({});

    const [selectedRole, setSelectedRole] = useState<string>('GÉNÉRAL');
    const [editingTplId, setEditingTplId] = useState<string | null>(null);
    const [newTplName, setNewTplName] = useState('');
    const [newTplService, setNewTplService] = useState<ShiftServiceType>('midi+soir');
    const [newTplSlots, setNewTplSlots] = useState<TimeSlot[]>([{ start: '10:00', end: '15:00' }]);
    const [newTplColor, setNewTplColor] = useState(PASTEL_COLORS[6]);

    const [weeklyDefaults, setWeeklyDefaults] = useState<Record<string, Record<string, string>>>({});
    // Codes d'absence : édités dans l'onglet « Codes d'absence » ci-dessous (ramenés ici le 2026-08-25).
    // Cet état reste nécessaire : persistSettings envoie toujours un payload COMBINÉ, donc enregistrer un
    // autre onglet réécrirait les codes. Il est resynchronisé via onSaved dès qu'on les modifie.
    const [absenceCodes, setAbsenceCodes] = useState<{ code: string; isFullDay: boolean; autoApply: boolean; color?: string }[]>([]);
    const [extraTypes, setExtraTypes] = useState<string[]>(['Hôtesse LBE', 'Agent de sécurité', 'Extra Service', 'Extra Cuisine']);
    const [newExtraType, setNewExtraType] = useState('');
    const [savingSettings, setSavingSettings] = useState(false);
    const [rolesOrder, setRolesOrder] = useState<string[]>([]);
    const [dragIdx, setDragIdx] = useState<number | null>(null);
    const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);
    const [changedDefaults, setChangedDefaults] = useState<Set<string>>(new Set());
    const [longAbsences, setLongAbsences] = useState<any[]>([]);
    const [editingAbsenceId, setEditingAbsenceId] = useState<string | null>(null);
    const [editStart, setEditStart] = useState('');
    const [editEnd, setEditEnd] = useState('');
    const [isSavingAbsence, setIsSavingAbsence] = useState(false);
    const [isLoadingAbsences, setIsLoadingAbsences] = useState(false);
    const [premiumRules, setPremiumRules] = useState<PremiumRules>(DEFAULT_PREMIUM_RULES);
    // Le temps de pause (maquette 26d). Comme les primes, il tient son propre etat : le payload combine
    // de persistSettings repart de la version en base et perdrait la saisie en cours.
    const [reglagesPause, setReglagesPause] = useState<ReglagesPause>(lireReglagesPause(null));
    const [savingPause, setSavingPause] = useState(false);
    const [savingPremiums, setSavingPremiums] = useState(false);
    // L'onglet « Codes d'absence » enregistre lui-meme (payload combine oblige), mais son bouton vit
    // desormais dans le pied de page de la modale, comme celui de tous les autres onglets.
    const absenceCodesSaveRef = React.useRef<(() => void) | null>(null);
    const [absenceCodesSaveState, setAbsenceCodesSaveState] = useState({ saving: false, saved: false });

    const orderedRolesForDisplay = useMemo(() => {
        if (!Array.isArray(roles) || roles.length === 0) return [] as { id: string; label: string }[];
        if (!Array.isArray(rolesOrder) || rolesOrder.length === 0) return [...roles];

        const ordered = rolesOrder
            .map((id) => roles.find((r) => String(r.id) === String(id)))
            .filter((r): r is { id: string; label: string } => Boolean(r));
        const remaining = roles.filter((r) => !rolesOrder.includes(String(r.id)));
        return [...ordered, ...remaining];
    }, [roles, rolesOrder]);

    useEffect(() => {
        if (isOpen) {
            loadData();
        }
    }, [isOpen]);

    useEffect(() => {
        if (isOpen && activeTab === 'longAbsences') {
            loadLongAbsences();
        }
    }, [isOpen, activeTab]);

    useEffect(() => {
        if (!roles || roles.length === 0) return;
        if (selectedRole === 'GÉNÉRAL') return;
        const exists = roles.some((r) => r.id === selectedRole);
        if (!exists) setSelectedRole(roles[0].id);
    }, [roles, selectedRole]);

    const loadData = async () => {
        try {
            const [tplData, rawSettings, absencesData] = await Promise.all([
                planningApi.getTemplates(),
                planningApi.getSettings(),
                planningApi.getAbsences()
            ]);
            setTemplates(Array.isArray(tplData) ? tplData : []);
            setLongAbsences(Array.isArray(absencesData) ? absencesData : []);

            const settings = (rawSettings && typeof rawSettings === 'object') ? rawSettings : {};
            const nextDefaults = (settings.weeklyDefaults && typeof settings.weeklyDefaults === 'object')
                ? settings.weeklyDefaults
                : {};
            const nextAbsences = Array.isArray(settings.absenceCodes) && settings.absenceCodes.length > 0
                ? settings.absenceCodes.map((c: any) => {
                    if (typeof c === 'string') return { code: c, isFullDay: true, autoApply: true };
                    // Extra safe extraction
                    let codeStr = 'ABS';
                    if (c && c.code) {
                        if (typeof c.code === 'object') codeStr = String(c.code.code || c.code.label || 'ABS');
                        else codeStr = String(c.code);
                    }
                    return { 
                        code: codeStr, 
                        isFullDay: !!c?.isFullDay,
                        autoApply: c.autoApply ?? !!c?.isFullDay,
                        color: c.color || (codeStr === 'REPOS' ? '#000000' : '#ffe39b')
                    };
                })
                : ABSENCE_TYPES.map(c => ({ code: c, isFullDay: true, autoApply: true, color: '#ffe39b' }));
            const nextExtraTypes = Array.isArray(settings.extraTypes) && settings.extraTypes.length > 0
                ? settings.extraTypes.map((c: any) => String(c)).filter(Boolean)
                : ['Hôtesse LBE', 'Agent de sécurité', 'Extra Service', 'Extra Cuisine'];
            const nextRolesOrder = Array.isArray(settings.rolesOrder) ? Array.from(new Set<string>(settings.rolesOrder.map((v: any) => String(v)))) : [];
            setReglagesPause(lireReglagesPause(settings));
            const nextPremiumRules: PremiumRules = settings.premiumRules && typeof settings.premiumRules === 'object'
                ? {
                    soir: { ...DEFAULT_PREMIUM_RULES.soir, ...(settings.premiumRules.soir || {}) },
                    nuit: { ...DEFAULT_PREMIUM_RULES.nuit, ...(settings.premiumRules.nuit || {}) },
                    weekend: { ...DEFAULT_PREMIUM_RULES.weekend, ...(settings.premiumRules.weekend || {}) },
                }
                : DEFAULT_PREMIUM_RULES;

            setSettingsPayload(settings);
            setWeeklyDefaults(nextDefaults);
            setAbsenceCodes(nextAbsences);
            setExtraTypes(nextExtraTypes);
            setRolesOrder(nextRolesOrder);
            setPremiumRules(nextPremiumRules);
            setChangedDefaults(new Set());
        } catch (e) {
            console.error('Failed to load planning settings', e);
        }
    };

    const loadLongAbsences = async () => {
        setIsLoadingAbsences(true);
        try {
            const data = await planningApi.getAbsences();
            setLongAbsences(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error(e);
        } finally {
            setIsLoadingAbsences(false);
        }
    };

    const persistSettings = async (nextDefaults: Record<string, Record<string, string>>, nextAbsences: { code: string; isFullDay: boolean }[], nextExtraTypes: string[], nextRolesOrder?: string[]) => {
        try {
            setSavingSettings(true);
            const payload: PlanningSettingsPayload = {
                ...settingsPayload,
                weeklyDefaults: nextDefaults,
                absenceCodes: nextAbsences,
                extraTypes: nextExtraTypes,
                rolesOrder: nextRolesOrder ?? rolesOrder,
            };
            await planningApi.saveSettings(payload);
            setSettingsPayload(payload);
            onDataChanged?.({ 
                applyWeeklyDefaults: true, 
                changedEmployees: Array.from(changedDefaults) 
            });
            setChangedDefaults(new Set());
        } catch (e) {
            console.error('Failed to save planning settings', e);
        } finally {
            setSavingSettings(false);
        }
    };

    const savePremiumRules = async (next: PremiumRules) => {
        setSavingPremiums(true);
        try {
            const payload: PlanningSettingsPayload = { ...settingsPayload, premiumRules: next };
            await planningApi.saveSettings(payload);
            setSettingsPayload(payload);
            setPremiumRules(next);
        } catch (e) {
            console.error('Failed to save premium rules', e);
        } finally {
            setSavingPremiums(false);
        }
    };

    const savePauseRules = async (next: ReglagesPause) => {
        setSavingPause(true);
        try {
            const payload: PlanningSettingsPayload = { ...settingsPayload, ...next };
            await planningApi.saveSettings(payload);
            setSettingsPayload(payload);
            setReglagesPause(next);
        } catch (e) {
            console.error('Failed to save pause settings', e);
        } finally {
            setSavingPause(false);
        }
    };

    const resetTemplateForm = () => {
        setEditingTplId(null);
        setNewTplName('');
        setNewTplService('midi+soir');
        setNewTplSlots([{ start: '10:00', end: '15:00' }]);
        setNewTplColor(PASTEL_COLORS[6]);
    };

    const handleSaveTemplate = async () => {
        if (!newTplName.trim()) return;
        try {
            let updated: Template[];
            if (editingTplId) {
                updated = templates.map((t) =>
                    t.id === editingTplId
                        ? {
                            ...t,
                            id: editingTplId,
                            role: selectedRole,
                            name: newTplName.trim(),
                            serviceType: newTplService,
                            slots: newTplSlots,
                            color: newTplColor,
                        }
                        : t
                );
            } else {
                const newTpl: Template = {
                    id: crypto.randomUUID(),
                    name: newTplName.trim(),
                    role: selectedRole,
                    serviceType: newTplService,
                    slots: newTplSlots,
                    color: newTplColor,
                };
                updated = [...templates, newTpl];
            }

            await planningApi.saveTemplates(updated);
            setTemplates(updated);
            resetTemplateForm();
            onDataChanged?.();
        } catch (err) {
            console.error(err);
        }
    };

    const handleStartEditAbsence = (abs: any) => {
        setEditingAbsenceId(abs.id);
        setEditStart(abs.start_date);
        setEditEnd(abs.end_date);
    };

    const handleSaveAbsenceDates = async (abs: any) => {
        if (!editStart || !editEnd) return;
        setIsSavingAbsence(true);
        try {
            await planningApi.saveAbsence({
                id: abs.id,
                employee_id: abs.employee_id,
                start_date: editStart,
                end_date: editEnd,
                absence_type: abs.absence_type
            });
            setEditingAbsenceId(null);
            loadLongAbsences();
            onDataChanged?.();
        } catch (e) {
            console.error('Failed to update absence', e);
            alert('Erreur lors de la modification');
        } finally {
            setIsSavingAbsence(false);
        }
    };

    const handleDeleteTemplate = async (id: string) => {
        const previous = templates;
        const removed = templates.find((t) => t.id === id);
        try {
            const updated = templates.filter((t) => t.id !== id);
            await planningApi.saveTemplates(updated);
            setTemplates(updated);
            if (editingTplId === id) resetTemplateForm();
            onDataChanged?.();
            pushUndo({
                message: `Modèle « ${removed?.name || 'sans nom'} » supprimé`,
                undo: async () => { await planningApi.saveTemplates(previous); setTemplates(previous); onDataChanged?.(); },
                undoneMessage: 'Modèle rétabli',
            });
        } catch (e) {
            console.error(e);
        }
    };

    const visibleTemplates = templates.filter((t) => t.role === selectedRole);

    const employeesForSelectedRole = useMemo(() => {
        const filtered = (Array.isArray(employees) ? employees : []).filter((e: any) => String(e?.position || '') === selectedRole);
        return [...filtered].sort((a, b) => {
            const nameA = `${a.last_name || ''} ${a.first_name || ''}`.trim().toUpperCase();
            const nameB = `${b.last_name || ''} ${b.first_name || ''}`.trim().toUpperCase();
            return nameA.localeCompare(nameB, 'fr', { sensitivity: 'base' });
        });
    }, [employees, selectedRole]);

    const getTemplateOptionsForRole = (roleId: string) => {
        return templates.filter((t) => t.role === roleId || t.role === 'GÉNÉRAL');
    };

    const updateDefaultForEmployeeDay = (employeeId: string, dayIndex: number, value: string) => {
        const dayKey = String(dayIndex);
        setWeeklyDefaults((prev) => ({
            ...prev,
            [employeeId]: {
                ...(prev[employeeId] || {}),
                [dayKey]: value,
            },
        }));
        setChangedDefaults(prev => new Set(prev).add(employeeId));
    };

    const addExtraType = () => {
        const code = newExtraType.trim();
        if (!code) return;
        if (extraTypes.includes(code)) return;
        setExtraTypes((prev) => [...prev, code]);
        setNewExtraType('');
    };

    const removeExtraType = (code: string) => {
        setExtraTypes((prev) => prev.filter((c) => c !== code));
    };

    const handleDeleteLongAbsence = (id: string) => {
        setConfirmRequest({
            title: 'Supprimer cette absence ?',
            description: <>L'absence sera retirée des futurs plannings. Les plannings déjà enregistrés ne sont pas modifiés.</>,
            confirmLabel: 'Supprimer l\'absence',
            onConfirm: () => deleteLongAbsenceNow(id),
        });
    };

    const deleteLongAbsenceNow = async (id: string) => {
        try {
            await planningApi.deleteAbsence(id);
            setLongAbsences(prev => prev.filter(a => a.id !== id));
        } catch (e) {
            console.error('Failed to delete long absence', e);
        }
    };

    const handleDropRoleAt = (targetIdx: number) => {
        if (dragIdx === null || dragIdx === targetIdx) {
            setDragIdx(null);
            setDragOverIdx(null);
            return;
        }

        const ids = orderedRolesForDisplay.map((r) => r.id);
        const [removed] = ids.splice(dragIdx, 1);
        ids.splice(targetIdx, 0, removed);
        setRolesOrder(ids);
        setDragIdx(null);
        setDragOverIdx(null);
    };

    if (!isOpen) return null;

    type Tab = typeof activeTab;
    // Les 7 sections du rail, dans l'ordre historique, avec un compteur quand il y en a un.
    const SECTIONS: { key: Tab; label: string; icon: React.ReactNode; count?: number; help: string }[] = [
        { key: 'templates', label: 'Modèles horaires', icon: <LayoutTemplate size={15} />, count: templates.length, help: 'Les plages horaires réutilisables, par poste.' },
        { key: 'defaults', label: 'Défauts hebdo', icon: <CalendarDays size={15} />, help: 'Les horaires par défaut de chaque employé, jour par jour.' },
        { key: 'absenceCodes', label: 'Codes d\'absence', icon: <UserX size={15} />, count: absenceCodes.length, help: 'Les codes posés dans une cellule à la place d\'un horaire.' },
        // Onglet retiré le 20/08 quand le catalogue Partenaires est devenu la source des types de
        // renfort. Remis le 27/08 à la demande d'Emeni : les deux doivent coexister — le catalogue
        // Partenaires pour ce qui se commande à un prestataire, cette liste-ci pour les renforts
        // qu'on note soi-même dans le planning sans passer par personne.
        { key: 'extraTypes', label: 'Types de renfort', icon: <UserPlus size={15} />, count: extraTypes.length, help: 'Les renforts notés directement dans la grille, sans passer par une agence.' },
        { key: 'longAbsences', label: 'Absences longue durée', icon: <CalendarOff size={15} />, count: longAbsences.length, help: 'Les absences déjà posées sur plusieurs semaines.' },
        { key: 'order', label: 'Ordre d\'affichage', icon: <ListOrdered size={15} />, help: 'L\'ordre des postes dans la grille : glissez-déposez les lignes pour le changer.' },
        { key: 'pause', label: 'Temps de pause', icon: <Coffee size={15} />, help: 'La pause déduite par journée travaillée. Le planning garde toujours la présence réelle : la déduction ne sort que dans l\'export.' },
        { key: 'premiums', label: 'Primes horaires', icon: <BadgePercent size={15} />, help: 'Majorations du soir, de nuit et du week-end appliquées au coût de la semaine. Nécessite un taux horaire renseigné sur la fiche de l\'employé.' },
        { key: 'backup', label: 'Sauvegarde', icon: <Archive size={15} />, help: "Recevoir le planning par e-mail, automatiquement, à l'heure de votre choix." },
    ];
    const current = SECTIONS.find((sec) => sec.key === activeTab) || SECTIONS[0];

    const footer = (
        <>
            <span className="text-[11px] text-content-muted font-mono">Échap pour fermer</span>
            <div className="flex flex-wrap items-center gap-2">
                <button type="button" onClick={onClose} className="h-9 px-4 rounded-full border border-border bg-surface text-[13px] font-semibold text-content whitespace-nowrap shrink-0 hover:border-content">Fermer</button>
                {activeTab !== 'templates' && activeTab !== 'longAbsences' && activeTab !== 'backup' && (
                    <button
                        type="button"
                        onClick={() => {
                            if (activeTab === 'absenceCodes') {
                                // L'onglet tient sa propre liste : on declenche SON enregistrement, pas le payload combine.
                                absenceCodesSaveRef.current?.();
                            } else if (activeTab === 'pause') {
                                savePauseRules(reglagesPause);
                            } else if (activeTab === 'premiums') {
                                // Les primes ont leur propre enregistrement : le payload combiné de
                                // persistSettings repartait de la version en base et perdait la saisie.
                                savePremiumRules(premiumRules);
                            } else if (activeTab === 'order') {
                                const orderedRoles = orderedRolesForDisplay.map((r) => r.id);
                                persistSettings(weeklyDefaults, absenceCodes, extraTypes, orderedRoles);
                                setRolesOrder(orderedRoles);
                            } else {
                                persistSettings(weeklyDefaults, absenceCodes, extraTypes);
                            }
                        }}
                        disabled={savingSettings || savingPremiums || savingPause || absenceCodesSaveState.saving}
                        className="h-9 inline-flex items-center gap-2 px-5 rounded-full bg-content text-surface text-[13px] font-semibold whitespace-nowrap shrink-0 hover:-translate-y-px transition-transform disabled:opacity-60"
                    >
                        <Save size={13} /> {(savingSettings || savingPremiums || savingPause || absenceCodesSaveState.saving)
                            ? 'Enregistrement…'
                            : (activeTab === 'absenceCodes' && absenceCodesSaveState.saved) ? 'Enregistré ✓' : 'Enregistrer'}
                    </button>
                )}
            </div>
        </>
    );

    return (
        // Pas de sous-titre de modale : l'explication appartient à la section ouverte, en tête du panneau droit.
        <Modal open={isOpen} onClose={onClose} size="2xl" widthPx={920} title="Paramètres du planning" footer={footer} footerLayout="split" flush>
            <div className="grid grid-cols-1 md:grid-cols-[224px_1fr] min-h-[420px]">
                {/* Rail vertical : les 7 sections, plus d'onglets hors champ */}
                <nav className="md:border-r border-b md:border-b-0 border-border bg-[#fbfcfd] dark:bg-surface-soft/60 p-2 flex md:flex-col gap-1 overflow-x-auto md:overflow-visible md:sticky md:top-0 md:self-start" aria-label="Sections">
                    {SECTIONS.map((sec) => {
                        const active = activeTab === sec.key;
                        return (
                            <button
                                key={sec.key}
                                type="button"
                                onClick={() => setActiveTab(sec.key)}
                                aria-current={active ? 'page' : undefined}
                                className={`flex items-center gap-2.5 w-full h-9 px-3 rounded-[9px] text-[13px] text-left whitespace-nowrap shrink-0 transition-colors ${active ? 'bg-ink text-white font-semibold' : 'text-[color:var(--text-secondary)] hover:bg-[#f1f3f6] dark:hover:bg-surface-soft hover:text-content'}`}
                            >
                                <span className="shrink-0">{sec.icon}</span>
                                <span className="flex-1 truncate">{sec.label}</span>
                                {sec.count !== undefined && sec.count > 0 && (
                                    <span className={`text-[11px] tabular-nums px-1.5 rounded-full ${active ? 'bg-surface/20' : 'bg-surface border border-border'}`}>{sec.count}</span>
                                )}
                            </button>
                        );
                    })}
                </nav>

                {/* Le contenu défile seul (brief §3 : max 520 px), le rail des sections reste en vue. */}
                <div className="px-5 py-[18px] text-xs min-w-0">
                    <div className="mb-4">
                        <h3 className="text-[14.5px] font-bold text-content">{current.label}</h3>
                        <p className="text-[12px] text-content-muted mt-0.5">{current.help}</p>
                    </div>
                    {(activeTab === 'templates' || activeTab === 'defaults') && (
                        <div className="flex flex-wrap gap-1.5 pb-0.5 mb-3">
                            <button
                                onClick={() => { setSelectedRole('GÉNÉRAL'); resetTemplateForm(); }}
                                className={`h-[30px] px-3.5 rounded-full text-[12px] font-semibold whitespace-nowrap shrink-0 border transition-all ${selectedRole === 'GÉNÉRAL' ? 'bg-content text-surface border-content' : 'bg-surface text-content-muted border-border hover:border-content'}`}
                            >
                                GÉNÉRAL
                            </button>
                            {roles.map((role) => (
                                <button
                                    key={role.id}
                                    onClick={() => { setSelectedRole(role.id); resetTemplateForm(); }}
                                    className={`h-[30px] px-3.5 rounded-full text-[12px] font-semibold whitespace-nowrap shrink-0 border transition-all ${selectedRole === role.id ? 'bg-content text-surface border-content' : 'bg-surface text-content-muted border-border hover:border-content'}`}
                                >
                                    {role.label}
                                </button>
                            ))}
                        </div>
                    )}

                    {activeTab === 'templates' && (
                        <div className="space-y-4">
                            {/* Liste des modèles du poste : une ligne par modèle (brief Planning §3) */}
                            <div className="rounded-xl border border-border bg-surface divide-y divide-border">
                                {visibleTemplates.length === 0 && (
                                    <p className="px-4 py-5 text-[12px] text-content-muted">Aucun modèle pour ce poste. Créez-en un ci-dessous.</p>
                                )}
                                {visibleTemplates.map((t) => (
                                    <div key={t.id} className="flex flex-wrap items-center gap-3 px-3.5 py-2.5 group">
                                        <span className="w-[13px] h-[13px] rounded-[4px] shrink-0 border border-black/10" style={{ backgroundColor: t.color || '#ccc' }} />
                                        <span className="text-[13px] font-semibold text-content truncate min-w-0 flex-1">{t.name}</span>
                                        <span className="h-[22px] px-2 inline-flex items-center rounded-full bg-surface-soft text-[11px] font-semibold text-content-muted whitespace-nowrap shrink-0">{(SERVICE_LABELS as Record<string, string>)[t.serviceType] || t.serviceType}</span>
                                        <span className="font-mono text-[11.5px] tabular-nums text-content-muted whitespace-nowrap shrink-0">{t.slots.map((sl) => `${sl.start}–${sl.end}`).join(' · ')}</span>
                                        <span className="flex items-center gap-1 shrink-0">
                                            <button
                                                type="button"
                                                title="Modifier"
                                                onClick={() => {
                                                    setEditingTplId(t.id);
                                                    setNewTplName(t.name);
                                                    setNewTplService(t.serviceType);
                                                    setNewTplSlots(t.slots);
                                                    setNewTplColor(t.color || PASTEL_COLORS[6]);
                                                }}
                                                className="w-[30px] h-[30px] rounded-lg flex items-center justify-center text-content-muted hover:bg-surface-soft hover:text-content"
                                            >
                                                <Edit2 size={13} />
                                            </button>
                                            <button type="button" title="Supprimer" onClick={() => handleDeleteTemplate(t.id)} className="w-[30px] h-[30px] rounded-lg flex items-center justify-center text-[#8f5450] hover:bg-state-danger-soft">
                                                <Trash2 size={13} />
                                            </button>
                                        </span>
                                    </div>
                                ))}
                            </div>

                            {/* Bloc de création / modification */}
                            <div className="rounded-xl border border-border bg-surface-soft/60 p-4 space-y-3">
                                <div className="text-[10px] font-bold uppercase tracking-[.09em] text-content-muted">{editingTplId ? 'Modifier le modèle' : 'Nouveau modèle'}</div>
                                <div className="grid grid-cols-1 sm:grid-cols-[1.4fr_1fr_80px] gap-3">
                                    <div>
                                        <label className="text-[12px] font-semibold text-content mb-1 block">Nom du modèle</label>
                                        <input value={newTplName} onChange={(e) => setNewTplName(e.target.value)} className="w-full h-9 border border-border bg-surface rounded-[9px] px-3 text-[13px] text-content outline-none focus:border-content" placeholder="Ex : Service du soir" />
                                    </div>
                                    <div>
                                        <label className="text-[12px] font-semibold text-content mb-1 block">Service</label>
                                        <select value={newTplService} onChange={(e) => setNewTplService(e.target.value as ShiftServiceType)} className="w-full h-9 border border-border bg-surface rounded-[9px] px-2 text-[13px] text-content outline-none focus:border-content">
                                            <option value="midi+soir">Midi + Soir</option>
                                            <option value="midi">Midi</option>
                                            <option value="soir">Soir</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="text-[12px] font-semibold text-content mb-1 block">Couleur</label>
                                        <div className="flex flex-wrap gap-1.5 pt-1">
                                            {PASTEL_COLORS.map((c) => (
                                                <button key={c} type="button" onClick={() => setNewTplColor(c)} aria-label={c} className={`w-5 h-5 rounded-full border-2 ${newTplColor === c ? 'border-content scale-110' : 'border-transparent'}`} style={{ backgroundColor: c }} />
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <label className="text-[12px] font-semibold text-content mb-1 block">Plages horaires</label>
                                    <div className="space-y-1.5">
                                        {newTplSlots.map((slot, i) => (
                                            <div key={i} className="flex flex-wrap gap-2 items-center">
                                                <input type="time" value={slot.start} onChange={(e) => { const n = [...newTplSlots]; n[i].start = e.target.value; setNewTplSlots(n); }} className="h-9 border border-border bg-surface rounded-[9px] px-2 text-[13px] tabular-nums text-content outline-none focus:border-content" />
                                                <span className="text-content-muted">–</span>
                                                <input type="time" value={slot.end} onChange={(e) => { const n = [...newTplSlots]; n[i].end = e.target.value; setNewTplSlots(n); }} className="h-9 border border-border bg-surface rounded-[9px] px-2 text-[13px] tabular-nums text-content outline-none focus:border-content" />
                                                <span className="h-[22px] px-2 inline-flex items-center rounded-full bg-surface text-[11px] font-semibold text-content-muted border border-border whitespace-nowrap shrink-0">{(SERVICE_LABELS as Record<string, string>)[newTplService] || newTplService}</span>
                                                {newTplSlots.length > 1 && (
                                                    <button type="button" title="Retirer la plage" onClick={() => { const n = [...newTplSlots]; n.splice(i, 1); setNewTplSlots(n); }} className="w-[30px] h-[30px] rounded-lg flex items-center justify-center text-content-muted hover:text-[#8f5450] hover:bg-state-danger-soft"><X size={14} /></button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                    <button type="button" onClick={() => setNewTplSlots([...newTplSlots, { start: '18:00', end: '23:00' }])} className="h-8 px-2 mt-2 rounded-full text-[12px] font-semibold text-content-muted hover:text-content hover:bg-surface-soft inline-flex items-center gap-1"><Plus size={13} /> Ajouter une plage</button>
                                </div>
                                <div className="flex flex-wrap justify-end gap-2 pt-1">
                                    {editingTplId && <button type="button" onClick={resetTemplateForm} className="h-9 px-4 rounded-full border border-border bg-surface text-[13px] font-semibold text-content whitespace-nowrap shrink-0 hover:border-content">Annuler</button>}
                                    <button type="button" onClick={handleSaveTemplate} className="h-9 px-5 rounded-full bg-content text-surface text-[13px] font-semibold whitespace-nowrap shrink-0 hover:-translate-y-px transition-transform">{editingTplId ? 'Enregistrer le modèle' : 'Créer le modèle'}</button>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'defaults' && (
                        <div className="space-y-3">
                            <div className="bg-surface rounded-xl border border-border overflow-hidden">
                                <div className="overflow-x-auto">
                                    <table className="w-full text-[12px] min-w-[720px]">
                                        <thead className="bg-surface-soft border-b border-slate-200 dark:border-border sticky top-0 z-20">
                                            <tr>
                                                <th className="px-3 py-2 text-left text-[10px] font-bold uppercase tracking-[.09em] text-content-muted sticky left-0 bg-surface-soft z-30 whitespace-nowrap">Salarié · {selectedRole === 'GÉNÉRAL' ? 'Général' : (roles.find((r) => r.id === selectedRole)?.label || selectedRole)}</th>
                                                {DAYS.map((day) => (
                                                    <th key={day} className="p-2 text-center text-slate-600 dark:text-slate-400 font-bold">{day}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100 dark:divide-white/5">
                                            {employeesForSelectedRole.length === 0 ? (
                                                <tr>
                                                    <td colSpan={8} className="p-4 text-center text-slate-400 italic">Aucun employé pour ce poste.</td>
                                                </tr>
                                            ) : employeesForSelectedRole.map((emp: any) => {
                                                const lastName = (emp.last_name || '').toUpperCase();
                                                const firstName = emp.first_name || '';
                                                const employeeName = `${lastName} ${firstName}`.trim() || String(emp?.name || 'Employé');
                                                const empDefaults = weeklyDefaults[String(emp.id)] || {};
                                                const options = getTemplateOptionsForRole(String(emp?.position || selectedRole));
                                                return (
                                                    <tr key={String(emp.id)} className="hover:bg-slate-50/60 dark:hover:bg-surface-soft transition-colors">
                                                        <td className="p-2 text-slate-800 dark:text-slate-200 font-bold sticky left-0 bg-surface z-10">{employeeName}</td>
                                                        {DAYS.map((_, dayIdx) => (
                                                            <td key={`${emp.id}-${dayIdx}`} className="p-1">
                                                                <select
                                                                    value={empDefaults[String(dayIdx)] || 'REPOS'}
                                                                    onChange={(e) => updateDefaultForEmployeeDay(String(emp.id), dayIdx, e.target.value)}
                                                                    className="w-full border border-slate-200 dark:border-border bg-surface rounded px-1.5 py-1 text-[10px] text-slate-800 dark:text-content outline-none focus:border-black dark:focus:border-border/30"
                                                                >
                                                                    <option value="REPOS">REPOS</option>
                                                                    {options.map((t) => (
                                                                        <option key={t.id} value={t.id}>{t.name}</option>
                                                                    ))}
                                                                </select>
                                                            </td>
                                                        ))}
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* onSaved : sans cette remontée, modifier un code puis enregistrer un autre onglet
                        réécrirait l'ancienne liste par-dessus (payload combiné). */}
                    {activeTab === 'absenceCodes' && (
                        <AbsenceCodesSettings
                            onSaved={setAbsenceCodes}
                            saveRef={absenceCodesSaveRef}
                            onSavingChange={setAbsenceCodesSaveState}
                        />
                    )}
                    {activeTab === 'extraTypes' && (
                        <div className="space-y-4">
                            <div className="bg-surface rounded-xl border border-slate-200 dark:border-border p-4 shadow-sm">
                                <div className="flex items-center gap-2 mb-1">
                                    <div className="w-7 h-7 rounded-lg bg-purple-500/10 flex items-center justify-center">
                                        <span className="text-purple-500 text-xs font-black">+</span>
                                    </div>
                                    <div>
                                        <h3 className="text-sm font-bold text-slate-800 dark:text-content">Extras / Renfort</h3>
                                        <p className="text-[10px] text-slate-500 dark:text-slate-400">Hôtesse, Sécurité, Extra Cuisine… Définissez les types de renfort utilisés dans votre planning. Ils s'ajoutent à ceux de votre catalogue Partenaires Extras cochés « Planning ».</p>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-surface rounded-xl border border-slate-200 dark:border-border p-4 shadow-sm">
                                <div className="flex flex-wrap gap-2 mb-4">
                                    <input value={newExtraType} onChange={(e) => setNewExtraType(e.target.value)} placeholder="Nouveau type (ex: Hôtesse, Vigile…)" className="flex-1 border border-slate-200 dark:border-border bg-surface rounded-lg px-3 py-2 text-sm text-slate-800 dark:text-content outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-400/20 transition-all" onKeyDown={e => e.key === 'Enter' && addExtraType()} />
                                    <button type="button" onClick={addExtraType} className="h-9 px-4 rounded-full bg-content text-surface text-[13px] font-semibold whitespace-nowrap shrink-0 flex items-center gap-1.5"><Plus size={14} /> Ajouter</button>
                                </div>
                                <div className="space-y-2">
                                    {extraTypes.length === 0 ? (
                                        <div className="text-center py-8 text-slate-400 italic text-xs">Aucun type de renfort configuré.</div>
                                    ) : extraTypes.map((code) => (
                                        <div key={code} className="flex items-center justify-between p-3 rounded-xl border border-slate-100 dark:border-border bg-surface-soft/50 group hover:border-purple-200 dark:hover:border-purple-500/20 transition-all">
                                            <span className="font-bold text-slate-700 dark:text-slate-200 text-sm">{code}</span>
                                            <button onClick={() => removeExtraType(code)} className="text-slate-300 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"><Trash2 size={14} /></button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Onglet autonome : il a ses propres boutons Enregistrer / Envoyer maintenant (pas le pied commun). */}
                    {activeTab === 'backup' && <PlanningBackupSettings />}

                    {activeTab === 'pause' && (
                        <PauseSettings
                            reglages={reglagesPause}
                            onChange={(partiel) => setReglagesPause((prev) => ({ ...prev, ...partiel }))}
                        />
                    )}

                    {activeTab === 'premiums' && (
                        <div className="space-y-4">
                            {/* Une carte par prime (brief §3) : le titre et l'explication de la section
                                sont déjà en haut du panneau, pas de carte d'en-tête en doublon. */}
                            <div className="space-y-3">
                                <div className={`rounded-xl border border-border bg-surface p-4 transition-opacity ${premiumRules.soir.enabled ? '' : 'opacity-50'}`}>
                                    <div className="flex-1">
                                        <label className="flex items-center justify-between gap-3 cursor-pointer mb-3">
                                            <span className="text-[13.5px] font-semibold text-content">Prime de soir</span>
                                            <span className="relative inline-flex items-center shrink-0">
                                            <input type="checkbox" role="switch" aria-label="Prime de soir" checked={premiumRules.soir.enabled} onChange={(e) => setPremiumRules({ ...premiumRules, soir: { ...premiumRules.soir, enabled: e.target.checked } })} className="peer sr-only" />
                                            <span className="w-[38px] h-[22px] rounded-full bg-surface-soft border border-border transition-colors peer-checked:bg-accent peer-checked:border-accent peer-focus-visible:shadow-[0_0_0_3px_rgba(15,20,25,.16)]" />
                                            <span className="absolute left-[3px] top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-4" />
                                            </span>
                                        </label>
                                        <div className="flex flex-wrap items-center gap-2">
                                            <span className="text-[12px] text-content-muted">À partir de</span>
                                            <input type="time" value={premiumRules.soir.afterHour} disabled={!premiumRules.soir.enabled} onChange={(e) => setPremiumRules({ ...premiumRules, soir: { ...premiumRules.soir, afterHour: e.target.value } })} className="h-9 bg-surface border border-border rounded-[9px] px-2.5 text-[13px] tabular-nums text-content outline-none focus:border-content disabled:bg-surface-soft disabled:text-content-muted disabled:cursor-not-allowed outline-none" />
                                            <span className="text-[12px] text-content-muted">majoration de</span>
                                            <input type="number" min={0} max={100} value={premiumRules.soir.percent} disabled={!premiumRules.soir.enabled} onChange={(e) => setPremiumRules({ ...premiumRules, soir: { ...premiumRules.soir, percent: Math.max(0, Number(e.target.value) || 0) } })} className="w-16 h-9 bg-surface border border-border rounded-[9px] px-2.5 text-[13px] tabular-nums text-content text-right outline-none focus:border-content disabled:bg-surface-soft disabled:text-content-muted disabled:cursor-not-allowed" />
                                            <span className="text-[12px] text-content-muted">%</span>
                                        </div>
                                    </div>
                                </div>

                                <div className={`rounded-xl border border-border bg-surface p-4 transition-opacity ${premiumRules.nuit.enabled ? '' : 'opacity-50'}`}>
                                    <div className="flex-1">
                                        <label className="flex items-center justify-between gap-3 cursor-pointer mb-3">
                                            <span className="text-[13.5px] font-semibold text-content">Prime de nuit</span>
                                            <span className="relative inline-flex items-center shrink-0">
                                            <input type="checkbox" role="switch" aria-label="Prime de nuit" checked={premiumRules.nuit.enabled} onChange={(e) => setPremiumRules({ ...premiumRules, nuit: { ...premiumRules.nuit, enabled: e.target.checked } })} className="peer sr-only" />
                                            <span className="w-[38px] h-[22px] rounded-full bg-surface-soft border border-border transition-colors peer-checked:bg-accent peer-checked:border-accent peer-focus-visible:shadow-[0_0_0_3px_rgba(15,20,25,.16)]" />
                                            <span className="absolute left-[3px] top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-4" />
                                            </span>
                                        </label>
                                        <div className="flex flex-wrap items-center gap-2">
                                            <span className="text-[12px] text-content-muted">À partir de</span>
                                            <input type="time" value={premiumRules.nuit.afterHour} disabled={!premiumRules.nuit.enabled} onChange={(e) => setPremiumRules({ ...premiumRules, nuit: { ...premiumRules.nuit, afterHour: e.target.value } })} className="h-9 bg-surface border border-border rounded-[9px] px-2.5 text-[13px] tabular-nums text-content outline-none focus:border-content disabled:bg-surface-soft disabled:text-content-muted disabled:cursor-not-allowed outline-none" />
                                            <span className="text-[12px] text-content-muted">majoration de</span>
                                            <input type="number" min={0} max={100} value={premiumRules.nuit.percent} disabled={!premiumRules.nuit.enabled} onChange={(e) => setPremiumRules({ ...premiumRules, nuit: { ...premiumRules.nuit, percent: Math.max(0, Number(e.target.value) || 0) } })} className="w-16 h-9 bg-surface border border-border rounded-[9px] px-2.5 text-[13px] tabular-nums text-content text-right outline-none focus:border-content disabled:bg-surface-soft disabled:text-content-muted disabled:cursor-not-allowed" />
                                            <span className="text-[12px] text-content-muted">%</span>
                                        </div>
                                    </div>
                                </div>

                                <div className={`rounded-xl border border-border bg-surface p-4 transition-opacity ${premiumRules.weekend.enabled ? '' : 'opacity-50'}`}>
                                    <div className="flex-1">
                                        <label className="flex items-center justify-between gap-3 cursor-pointer mb-3">
                                            <span className="text-[13.5px] font-semibold text-content">Prime de weekend</span>
                                            <span className="relative inline-flex items-center shrink-0">
                                            <input type="checkbox" role="switch" aria-label="Prime de weekend" checked={premiumRules.weekend.enabled} onChange={(e) => setPremiumRules({ ...premiumRules, weekend: { ...premiumRules.weekend, enabled: e.target.checked } })} className="peer sr-only" />
                                            <span className="w-[38px] h-[22px] rounded-full bg-surface-soft border border-border transition-colors peer-checked:bg-accent peer-checked:border-accent peer-focus-visible:shadow-[0_0_0_3px_rgba(15,20,25,.16)]" />
                                            <span className="absolute left-[3px] top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-4" />
                                            </span>
                                        </label>
                                        <div className="flex flex-wrap items-center gap-2">
                                            <span className="text-[12px] text-content-muted">Samedi et dimanche, majoration de</span>
                                            <input type="number" min={0} max={100} value={premiumRules.weekend.percent} disabled={!premiumRules.weekend.enabled} onChange={(e) => setPremiumRules({ ...premiumRules, weekend: { ...premiumRules.weekend, percent: Math.max(0, Number(e.target.value) || 0) } })} className="w-16 h-9 bg-surface border border-border rounded-[9px] px-2.5 text-[13px] tabular-nums text-content text-right outline-none focus:border-content disabled:bg-surface-soft disabled:text-content-muted disabled:cursor-not-allowed" />
                                            <span className="text-[12px] text-content-muted">%</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Enregistrement par le bouton du pied (brief §3 : un seul pied fixe). */}
                                <p className="text-[12px] text-content-muted">Un créneau qui commence après l'heure « nuit » et tombe un weekend cumule les deux majorations.</p>
                            </div>
                        </div>
                    )}

                    {activeTab === 'longAbsences' && (
                        <div className="space-y-4">
                            <div className="rounded-xl border border-border bg-surface-soft/60 p-3.5">
                                <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-1">Gérez ici les absences déjà posées. Pour en ajouter une nouvelle, utilisez le bouton "Ajouter" sur le planning principal.</p>
                            </div>

                            <div className="bg-surface rounded-xl border border-slate-200 dark:border-border overflow-hidden shadow-sm">
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left border-collapse">
                                        <thead className="bg-surface-soft border-b border-slate-200 dark:border-border sticky top-0 z-10">
                                            <tr>
                                                <th className="px-4 py-3 text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest">Employé</th>
                                                <th className="px-4 py-3 text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest">Type</th>
                                                <th className="px-4 py-3 text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest">Début</th>
                                                <th className="px-4 py-3 text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest">Fin</th>
                                                <th className="px-4 py-3 text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest text-right">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100 dark:divide-white/5">
                                            {(() => {
                                                const todayStr = format(new Date(), 'yyyy-MM-dd');
                                                const filteredAbsences = longAbsences.filter((abs) => !abs.end_date || abs.end_date >= todayStr);

                                                if (filteredAbsences.length === 0) {
                                                    return (
                                                        <tr>
                                                            <td colSpan={5} className="px-4 py-10 text-center text-slate-400 italic text-xs">Aucune absence longue durée en cours ou à venir.</td>
                                                        </tr>
                                                    );
                                                }

                                                return filteredAbsences.map((abs) => {
                                                    const emp = employees.find(e => String(e.id) === String(abs.employee_id));
                                                    const empName = emp ? `${(emp.last_name || '').toUpperCase()} ${emp.first_name || ''}`.trim() : 'Employé inconnu';
                                                    
                                                    return (
                                                    <tr key={abs.id} className="hover:bg-slate-50 dark:hover:bg-surface-soft transition-colors">
                                                        <td className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200 text-xs">{empName}</td>
                                                        <td className="px-4 py-3">
                                                            <span className="bg-orange-100 dark:bg-orange-500/20 text-orange-600 dark:text-orange-400 px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-wider">{abs.absence_type}</span>
                                                        </td>
                                                        <td className="px-4 py-3 text-slate-600 dark:text-slate-400 text-xs">
                                                            {editingAbsenceId === abs.id ? (
                                                                <input 
                                                                    type="date" 
                                                                    className="bg-surface-soft border border-slate-200 dark:border-border rounded px-2 py-1 text-xs outline-none focus:border-brand-500"
                                                                    value={editStart}
                                                                    onChange={(e) => setEditStart(e.target.value)}
                                                                />
                                                            ) : format(parseISO(abs.start_date), 'dd/MM/yyyy')}
                                                        </td>
                                                        <td className="px-4 py-3 text-slate-600 dark:text-slate-400 text-xs">
                                                            {editingAbsenceId === abs.id ? (
                                                                <input 
                                                                    type="date" 
                                                                    className="bg-surface-soft border border-slate-200 dark:border-border rounded px-2 py-1 text-xs outline-none focus:border-brand-500"
                                                                    value={editEnd}
                                                                    onChange={(e) => setEditEnd(e.target.value)}
                                                                />
                                                            ) : format(parseISO(abs.end_date), 'dd/MM/yyyy')}
                                                        </td>
                                                        <td className="px-4 py-3 text-right">
                                                            <div className="flex items-center justify-end gap-1">
                                                                {editingAbsenceId === abs.id ? (
                                                                    <>
                                                                        <button 
                                                                            onClick={() => handleSaveAbsenceDates(abs)}
                                                                            disabled={isSavingAbsence}
                                                                            className="p-1.5 text-emerald-500 hover:bg-emerald-50 dark:hover:bg-emerald-500/10 rounded-lg transition-all"
                                                                            title="Enregistrer"
                                                                        >
                                                                            {isSavingAbsence ? <Clock size={16} className="animate-spin" /> : <Check size={16} />}
                                                                        </button>
                                                                        <button 
                                                                            onClick={() => setEditingAbsenceId(null)}
                                                                            className="p-1.5 text-slate-400 hover:bg-slate-100 dark:hover:bg-surface-soft rounded-lg transition-all"
                                                                            title="Annuler"
                                                                        >
                                                                            <X size={16} />
                                                                        </button>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <button 
                                                                            onClick={() => handleStartEditAbsence(abs)}
                                                                            className="p-1.5 text-slate-300 hover:text-brand-500 hover:bg-brand-600/10 rounded-lg transition-all"
                                                                            title="Modifier les dates"
                                                                        >
                                                                            <Edit2 size={16} />
                                                                        </button>
                                                                        <button 
                                                                            onClick={() => handleDeleteLongAbsence(abs.id)}
                                                                            className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-lg transition-all"
                                                                            title="Supprimer"
                                                                        >
                                                                            <Trash2 size={16} />
                                                                        </button>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            });
                                        })()}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'order' && (
                        /* Lignes glissables (brief §3) : poignée, nom du groupe, effectif à droite.
                           La ligne saisie prend la bordure encre et le fond gris du socle. */
                        <div className="space-y-1.5">
                            {orderedRolesForDisplay.length === 0 && (
                                <p className="text-[12px] text-content-muted">Aucun poste à ordonner pour l'instant.</p>
                            )}
                            {orderedRolesForDisplay.map((role, idx) => {
                                const headcount = (Array.isArray(employees) ? employees : []).filter((e: any) => String(e?.position || '') === String(role.id)).length;
                                return (
                                    <div
                                        key={role.id}
                                        draggable
                                        onDragStart={() => setDragIdx(idx)}
                                        onDragOver={(e) => { e.preventDefault(); setDragOverIdx(idx); }}
                                        onDrop={() => handleDropRoleAt(idx)}
                                        onDragEnd={() => { setDragIdx(null); setDragOverIdx(null); }}
                                        className={`flex items-center gap-2.5 h-11 px-3 rounded-[9px] border transition-colors ${dragIdx === idx || dragOverIdx === idx ? 'border-content bg-surface-soft' : 'border-border bg-surface'} ${dragIdx === idx ? 'opacity-60' : 'cursor-grab active:cursor-grabbing'}`}
                                    >
                                        <GripVertical size={14} className="text-content-muted shrink-0" />
                                        <span className="text-[13px] font-semibold text-content flex-1 truncate">{role.label}</span>
                                        <span className="text-[12px] tabular-nums text-content-muted whitespace-nowrap shrink-0">{headcount} salarié{headcount > 1 ? 's' : ''}</span>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
            <ConfirmModal request={confirmRequest} onClose={() => setConfirmRequest(null)} />
        </Modal>
    );
};

export default PlanningSettings;
